/**
 * SettingsScreen
 * App settings and data export
 */

import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  SafeAreaView,
  Alert,
  Platform,
  ActivityIndicator,
  Switch,
  TextInput,
} from 'react-native';
import { SettingsScreenProps } from '../navigation/types';
import { exportToCSV, generateCSVFilename } from '../services/export/CSVExporter';
import { exportToJSON, generateJSONFilename } from '../services/export/JSONExporter';

export const SettingsScreen: React.FC<SettingsScreenProps> = ({ navigation }) => {
  const [isExporting, setIsExporting] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [notificationTime, setNotificationTime] = useState('09:00');

  const handleExportCSV = async () => {
    try {
      setIsExporting(true);

      const csvData = await exportToCSV();
      const filename = generateCSVFilename();

      if (Platform.OS === 'web') {
        // For web, trigger download
        const blob = new Blob([csvData], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);

        Alert.alert('Success', `Exported to ${filename}`);
      } else {
        // For native, you'd use react-native-fs or Share API
        Alert.alert('Export CSV', 'Native export coming soon!');
      }
    } catch (error) {
      Alert.alert('Error', 'Failed to export CSV');
      console.error(error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportJSON = async () => {
    try {
      setIsExporting(true);

      const jsonData = await exportToJSON();
      const filename = generateJSONFilename();

      if (Platform.OS === 'web') {
        // For web, trigger download
        const blob = new Blob([jsonData], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);

        Alert.alert('Success', `Exported to ${filename}`);
      } else {
        // For native, you'd use react-native-fs or Share API
        Alert.alert('Export JSON', 'Native export coming soon!');
      }
    } catch (error) {
      Alert.alert('Error', 'Failed to export JSON');
      console.error(error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleClearData = () => {
    if (Platform.OS === 'web') {
      // Use native browser confirm dialogs for better compatibility on web
      const firstConfirm = window.confirm(
        'Clear All Data?\n\nThis will delete all goals, completions, and journeys. This action cannot be undone.\n\nAre you sure you want to continue?'
      );

      if (!firstConfirm) return;

      const secondConfirm = window.confirm(
        'Final Warning!\n\nThis will PERMANENTLY delete ALL your data. This cannot be undone!\n\nType "DELETE" below if you are absolutely sure.'
      );

      if (secondConfirm) {
        localStorage.clear();
        alert('All data has been cleared. The app will now reload.');
        window.location.reload();
      }
    } else {
      // For native, use React Native Alert
      Alert.alert(
        'Clear All Data',
        'This will delete all goals, completions, and journeys. This action cannot be undone.',
        [
          { text: 'Cancel', style: 'cancel' },
          {
            text: 'Clear All Data',
            style: 'destructive',
            onPress: () => {
              // TODO: Add native storage clearing when implementing mobile
              Alert.alert('Success', 'All data has been cleared');
            },
          },
        ]
      );
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <ScrollView>
        {/* Export Section */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Data Export</Text>
          <Text style={styles.sectionDescription}>
            Download your complete goal history and completion data
          </Text>

          <TouchableOpacity
            style={styles.settingItem}
            onPress={() => navigation.navigate('Export')}
          >
            <View style={styles.settingInfo}>
              <Text style={styles.settingTitle}>Export Your Data</Text>
              <Text style={styles.settingDescription}>
                Download as CSV or JSON format
              </Text>
            </View>
            <Text style={styles.settingAction}>→</Text>
          </TouchableOpacity>
        </View>

        {/* Journey Management */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Journey Management</Text>

          <TouchableOpacity
            style={styles.settingItem}
            onPress={() => navigation.navigate('AccountList')}
          >
            <View style={styles.settingInfo}>
              <Text style={styles.settingTitle}>Manage Journeys</Text>
              <Text style={styles.settingDescription}>
                Switch, create, or delete journeys
              </Text>
            </View>
            <Text style={styles.settingAction}>→</Text>
          </TouchableOpacity>
        </View>

        {/* Goal Management */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Goal Management</Text>

          <TouchableOpacity
            style={styles.settingItem}
            onPress={() => navigation.navigate('GoalList')}
          >
            <View style={styles.settingInfo}>
              <Text style={styles.settingTitle}>Manage Goals</Text>
              <Text style={styles.settingDescription}>
                Edit or delete your goals
              </Text>
            </View>
            <Text style={styles.settingAction}>→</Text>
          </TouchableOpacity>
        </View>

        {/* AI Encouragement */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>AI Encouragement</Text>

          <TouchableOpacity
            style={styles.settingItem}
            onPress={() => navigation.navigate('AISettings')}
          >
            <View style={styles.settingInfo}>
              <Text style={styles.settingTitle}>AI Settings</Text>
              <Text style={styles.settingDescription}>
                Configure AI-powered encouragement messages
              </Text>
            </View>
            <Text style={styles.settingAction}>→</Text>
          </TouchableOpacity>
        </View>

        {/* Notifications */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Daily Reminders</Text>
          <Text style={styles.sectionDescription}>
            Get notified to complete your daily goals
          </Text>

          <View style={styles.settingRow}>
            <View style={styles.settingInfo}>
              <Text style={styles.settingTitle}>Enable Reminders</Text>
              <Text style={styles.settingDescription}>
                Daily notification to track your goals
              </Text>
            </View>
            <Switch
              value={notificationsEnabled}
              onValueChange={(value) => {
                setNotificationsEnabled(value);
                if (value && Platform.OS === 'web') {
                  Alert.alert(
                    'Notifications',
                    'Browser notifications are enabled. Make sure to allow notifications in your browser settings.',
                    [{ text: 'OK' }]
                  );
                }
              }}
            />
          </View>

          {notificationsEnabled && (
            <View style={styles.timePickerContainer}>
              <Text style={styles.timeLabel}>Reminder Time:</Text>
              <TextInput
                style={styles.timeInput}
                value={notificationTime}
                onChangeText={setNotificationTime}
                placeholder="09:00"
                keyboardType={Platform.OS === 'web' ? 'default' : 'numbers-and-punctuation'}
              />
              <Text style={styles.timeHint}>
                {Platform.OS === 'web'
                  ? 'Note: Native push notifications require the mobile app'
                  : 'Format: HH:MM (24-hour)'}
              </Text>
            </View>
          )}
        </View>

        {/* App Info */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>About</Text>

          <View style={styles.settingItem}>
            <View style={styles.settingInfo}>
              <Text style={styles.settingTitle}>Version</Text>
              <Text style={styles.settingDescription}>1.0.0 (MVP)</Text>
            </View>
          </View>

          <View style={styles.settingItem}>
            <View style={styles.settingInfo}>
              <Text style={styles.settingTitle}>Storage</Text>
              <Text style={styles.settingDescription}>
                {Platform.OS === 'web' ? 'localStorage' : 'MMKV/AsyncStorage'}
              </Text>
            </View>
          </View>

          <View style={styles.settingItem}>
            <View style={styles.settingInfo}>
              <Text style={styles.settingTitle}>Platform</Text>
              <Text style={styles.settingDescription}>
                {Platform.OS === 'web' ? 'Web' : Platform.OS === 'ios' ? 'iOS' : 'Android'}
              </Text>
            </View>
          </View>
        </View>

        {/* Danger Zone */}
        <View style={styles.section}>
          <Text style={[styles.sectionTitle, styles.dangerTitle]}>Danger Zone</Text>

          <TouchableOpacity
            style={[styles.settingItem, styles.dangerItem]}
            onPress={handleClearData}
          >
            <View style={styles.settingInfo}>
              <Text style={[styles.settingTitle, styles.dangerText]}>
                Clear All Data
              </Text>
              <Text style={styles.settingDescription}>
                Delete all goals, completions, and journeys
              </Text>
            </View>
            <Text style={[styles.settingAction, styles.dangerText]}>⚠️</Text>
          </TouchableOpacity>
        </View>

        {/* Footer */}
        <View style={styles.footer}>
          <Text style={styles.footerText}>
            EverydayGoal - One thing. Every day.
          </Text>
          <Text style={styles.footerText}>
            Made with focus and simplicity
          </Text>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  section: {
    marginTop: 24,
    marginHorizontal: 16,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#666',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 8,
  },
  sectionDescription: {
    fontSize: 14,
    color: '#999',
    marginBottom: 12,
    lineHeight: 20,
  },
  settingItem: {
    backgroundColor: '#fff',
    padding: 16,
    borderRadius: 8,
    marginBottom: 8,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    ...Platform.select({
      ios: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.1,
        shadowRadius: 2,
      },
      android: {
        elevation: 2,
      },
      web: {
        boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
      },
    }),
  },
  settingInfo: {
    flex: 1,
    marginRight: 12,
  },
  settingTitle: {
    fontSize: 16,
    fontWeight: '500',
    color: '#333',
    marginBottom: 4,
  },
  settingDescription: {
    fontSize: 14,
    color: '#666',
    lineHeight: 20,
  },
  settingAction: {
    fontSize: 20,
    color: '#007AFF',
  },
  dangerTitle: {
    color: '#FF3B30',
  },
  dangerItem: {
    borderWidth: 1,
    borderColor: '#FFE5E5',
  },
  dangerText: {
    color: '#FF3B30',
  },
  footer: {
    padding: 32,
    alignItems: 'center',
  },
  footerText: {
    fontSize: 12,
    color: '#999',
    textAlign: 'center',
    lineHeight: 18,
  },
  settingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  timePickerContainer: {
    marginTop: 16,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: '#f0f0f0',
  },
  timeLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
    marginBottom: 8,
  },
  timeInput: {
    backgroundColor: '#f9f9f9',
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    borderWidth: 1,
    borderColor: '#e0e0e0',
    marginBottom: 8,
  },
  timeHint: {
    fontSize: 12,
    color: '#999',
    fontStyle: 'italic',
  },
});
